import {
  PLATFORM_USER_ACCESS_FILTER_ALL,
  PLATFORM_USER_ACCESS_UI,
} from "pages/PlatformUserAccess/PlatformUserAccess.constants";
import {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_OPTIONS,
  CUSTOMER_ACCESS_FILTER_OPTIONS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
} from "common/Constants/AccessStatus.constants";

export const USERS_PAGE_CONSTANTS = {
  MFA_CONSENT:
    "By providing your mobile number, you consent to receive text messages from Inframark for account security, including Multi-Factor Authentication (MFA) codes. Message frequency varies. Reply STOP to unsubscribe. Standard message rates may apply.",
  SELECT_CUSTOMER_REQUIRED_ERROR:
    "Access restricted: You need to select a customer before proceeding.",
  MISSING_CUSTOMER_ID_ERROR:
    "Access restricted: Customer identifier is missing. Please select a customer from the header.",
};

export { PLATFORM_USER_ACCESS_FILTER_ALL as CUSTOMER_USERS_FILTER_ALL };

export {
  CUSTOMER_ACCESS_STATUS,
  CUSTOMER_ACCESS_STATUS_RAW,
  isCustomerAccessStatus,
  normalizeCustomerAccessStatus,
};

export const DEFAULT_CUSTOMER_USERS_LIST_FILTERS = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  role: "",
  accessStatus: "",
};

export const CUSTOMER_USERS_ACCESS_STATUS_FILTER_OPTIONS =
  CUSTOMER_ACCESS_FILTER_OPTIONS;

export const normalizeCustomerUsersListFilters = (rawColumnFilters = {}) => {
  const clean = (value) => {
    if (
      value == null ||
      value === "" ||
      value === PLATFORM_USER_ACCESS_FILTER_ALL
    ) {
      return "";
    }
    return String(value).trim();
  };
  return {
    email: clean(rawColumnFilters.email),
    firstName: clean(rawColumnFilters.firstName),
    lastName: clean(rawColumnFilters.lastName),
    phone: clean(rawColumnFilters.phone),
    role: clean(rawColumnFilters.role ?? rawColumnFilters.roles),
    accessStatus: clean(
      rawColumnFilters.accessStatus ?? rawColumnFilters.customerAccess
    ),
  };
};

export const mapCustomerUsersListFiltersToColumnFilters = (
  listFilters = DEFAULT_CUSTOMER_USERS_LIST_FILTERS
) => ({
  email: listFilters.email ?? "",
  firstName: listFilters.firstName ?? "",
  lastName: listFilters.lastName ?? "",
  phone: listFilters.phone ?? "",
  role: listFilters.role ?? "",
  accessStatus: listFilters.accessStatus ?? "",
});

export const CUSTOMER_USER_ACCESS_OPERATION = {
  ASSIGN_ROLES: "ASSIGN_ROLES",
  GRANT: "GRANT",
  REVOKE: "REVOKE",
  RESTORE: "RESTORE",
};

export const CUSTOMER_USER_LIST_ACTION = {
  REVOKE_ACCESS: "revoke",
  RESTORE_ACCESS: "restore",
  RESEND: "resend",
};

const isEditableCustomerUserStatus = (status) => {
  const normalized = normalizeCustomerAccessStatus(status);
  return (
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.ACTIVE) ||
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.SUSPENDED) ||
    isCustomerAccessStatus(normalized, CUSTOMER_ACCESS_STATUS.REVOKED)
  );
};

export const isCustomerUserRolesEditable = (user) =>
  isEditableCustomerUserStatus(user?.accountStatus) &&
  isEditableCustomerUserStatus(user?.accessStatus);

export const CUSTOMER_USER_ROLES_REQUIRED_ERROR =
  PLATFORM_USER_ACCESS_UI.customerAccessRolesRequiredError;

export const validateCustomerUserRolesSelection = (roles) => {
  const normalized = (Array.isArray(roles) ? roles : [])
    .map((role) => String(role).trim())
    .filter(Boolean);
  if (normalized.length === 0) {
    return CUSTOMER_USER_ROLES_REQUIRED_ERROR;
  }
  return null;
};

const normalizeRoleName = (role) => String(role ?? "").trim();

const grantableOptionValues = (grantableOptions = []) =>
  new Set(
    (grantableOptions ?? [])
      .map((option) => normalizeRoleName(option?.value))
      .filter(Boolean)
  );

export const getLockedRolesForSelection = (assignedRoles, grantableOptions = []) => {
  const grantableValues = grantableOptionValues(grantableOptions);
  return (Array.isArray(assignedRoles) ? assignedRoles : [])
    .map(normalizeRoleName)
    .filter((role) => role && !grantableValues.has(role));
};

export const mergeRolesWithLockedRoles = (nextSelection, lockedRoles = []) => {
  const locked = (Array.isArray(lockedRoles) ? lockedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean);
  const selected = (Array.isArray(nextSelection) ? nextSelection : [])
    .map(normalizeRoleName)
    .filter(Boolean);
  return [...new Set([...locked, ...selected])];
};

export const buildRowRoleDropdownOptions = (
  grantableOptions = [],
  lockedRoles = [],
  roleLabelMap = {}
) => {
  const lockedOptions = (Array.isArray(lockedRoles) ? lockedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean)
    .map((value) => ({
      value,
      label: roleLabelMap[value] ?? value,
      locked: true,
    }));

  const editableOptions = (grantableOptions ?? [])
    .map((option) => ({
      value: normalizeRoleName(option?.value),
      label: String(option?.label ?? option?.value ?? "").trim(),
      locked: false,
    }))
    .filter((option) => option.value)
    .filter(
      (option, index, list) =>
        list.findIndex((item) => item.value === option.value) === index
    )
    .filter(
      (option) =>
        !lockedOptions.some((lockedOption) => lockedOption.value === option.value)
    );

  return [...lockedOptions, ...editableOptions];
};

export const formatRoleSelectionDisplay = (
  selectedRoles = [],
  dropdownOptions = [],
  roleLabelMap = {}
) =>
  (Array.isArray(selectedRoles) ? selectedRoles : [])
    .map(normalizeRoleName)
    .filter(Boolean)
    .map((value) => {
      const fromOption = dropdownOptions.find(
        (option) => normalizeRoleName(option?.value) === value
      );
      return fromOption?.label ?? roleLabelMap[value] ?? value;
    })
    .join(", ");

export const deriveCustomerUserAccessVariant = (user) => {
  const raw = String(user?.accessStatus ?? user?.accountStatus ?? "").trim();
  const lower = raw.toLowerCase();
  if (
    lower === CUSTOMER_ACCESS_STATUS_RAW.REVOKED.toLowerCase() ||
    lower === CUSTOMER_ACCESS_STATUS.SUSPENDED.toLowerCase()
  ) {
    return CUSTOMER_ACCESS_STATUS.REVOKED;
  }
  if (
    lower === CUSTOMER_ACCESS_STATUS.INVITED.toLowerCase() ||
    lower === CUSTOMER_ACCESS_STATUS_RAW.PENDING.toLowerCase()
  ) {
    return CUSTOMER_ACCESS_STATUS.INVITED;
  }
  return CUSTOMER_ACCESS_STATUS.ACTIVE;
};

export const isCustomerUserEmailClickable = (user) =>
  !isCustomerAccessStatus(
    deriveCustomerUserAccessVariant(user),
    CUSTOMER_ACCESS_STATUS.INVITED
  );

export const getCustomerUserListRowAction = (user) => {
  const variant = deriveCustomerUserAccessVariant(user);
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.ACTIVE)) {
    return {
      label: "Revoke Access",
      action: CUSTOMER_USER_LIST_ACTION.REVOKE_ACCESS,
    };
  }
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.REVOKED)) {
    return {
      label: "Restore Access",
      action: CUSTOMER_USER_LIST_ACTION.RESTORE_ACCESS,
    };
  }
  if (isCustomerAccessStatus(variant, CUSTOMER_ACCESS_STATUS.INVITED)) {
    return {
      label: "Resend",
      action: CUSTOMER_USER_LIST_ACTION.RESEND,
    };
  }
  return null;
};
export const getCustomerRowAction = (accessStatus) => {
  if (accessStatus === CUSTOMER_ACCESS_STATUS.ACTIVE) {
    return {
      label: "Revoke Access",
      action: CUSTOMER_USER_LIST_ACTION.REVOKE_ACCESS,
    };
  }
  if (accessStatus === CUSTOMER_ACCESS_STATUS.REVOKED) {
    return {
      label: "Restore Access",
      action: CUSTOMER_USER_LIST_ACTION.RESTORE_ACCESS,
    };
  }
  return null;
};